import { message, Select, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  useWirePayments,
  WIRE_PAYMENT_PRISM_STATUSES,
  WIRE_PAYMENT_PRISM_STATUSES_TEXT,
  WIRE_PAYMENT_PROCESSING_STATUSES,
  WIRE_PAYMENT_PROCESSING_STATUSES_TEXT,
  WIRE_PAYMENT_STATUSES,
  WirePayment,
} from './utils/useWirePayments';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { WirePaymentsService } from 'app/services/wirePayments';
import { AppURLs } from 'app/utils/appURLs';
import WirePaymentsFilter from './WirePaymentsFilter';

const WirePayments = () => {
  const navigate = useNavigate();
  const [filterParams, setFilterParams] = useState({});
  const {
    wirePayments,
    loading,
    currentPage,
    total,
    pageSize,
    fetchWirePayments,
  } = useWirePayments();

  const handleProcessingStatusChange = async (
    newStatus: string,
    record: WirePayment
  ) => {
    try {
      await new WirePaymentsService().updateProcessingStatus(
        record._id,
        newStatus,
        record.processingStatus
      );
      message.success('Processing status updated successfully');
      fetchWirePayments(); // Refresh the table
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || 'Failed to update processing status'
      );
    }
  };

  const handleFilter = (params: {
    search?: string;
    processingStatus?: WIRE_PAYMENT_PROCESSING_STATUSES[];
    status?: WIRE_PAYMENT_STATUSES[];
    depositedDateRange?: [string, string];
    rejectedDateRange?: [string, string];
    prismStatus?: string[];
    contractCare?: boolean;
  }) => {
    setFilterParams(params);
    fetchWirePayments({ ...params, page: 1 });
  };

  const columns: ColumnsType<WirePayment> = [
    {
      title: 'Property Address',
      dataIndex: 'propertyAddress',
      key: 'propertyAddress',
      render: (text: string, record: WirePayment) => (
        <div className="flex items-center">
          {record.transactionId && (
            <Link
              to={`/compliance/dashboard/transactions/documents?transactionID=${record.transactionId}`}
              className="mr-2 text-blue-500 hover:text-blue-700"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="text-blue-500" size={16} />
            </Link>
          )}
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Dotloop Link',
      key: 'loopLink',
      render: (_, record: WirePayment) => {
        if (!record.transaction?.externalSourceID) return 'N/A';
        return (
          <Link
            to={`https://www.dotloop.com/m/loop?viewId=${record.transaction?.externalSourceID}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="inline-block" size={16} />
          </Link>
        );
      },
    },
    {
      title: 'Agent Email',
      dataIndex: ['primaryAgent', 'email'],
      key: 'agentEmail',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => {
        const absAmount = Math.abs(amount);
        return amount < 0
          ? `-$${absAmount.toLocaleString()}`
          : `$${amount.toLocaleString()}`;
      },
    },
    {
      title: 'Payment Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag
          color={status === WIRE_PAYMENT_STATUSES.PROCESSED ? 'green' : 'red'}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Prism Status',
      dataIndex: ['processingStatus', 'externalStatuses', 'PRISM'],
      key: 'processingStatus.externalStatuses.PRISM',
      render: (prismStatus: string, record: WirePayment) => {
        const currentProcessingStatus = record.processingStatus || {
          status: '',
          missingDocs: [],
          externalStatuses: {},
        };

        const currentExternalStatuses =
          currentProcessingStatus.externalStatuses || {};

        return (
          <Select
            value={prismStatus || ''}
            onChange={(newPrismStatus) => {
              const updatedProcessingStatus = {
                ...currentProcessingStatus,
                externalStatuses: {
                  ...currentExternalStatuses,
                  PRISM: newPrismStatus,
                },
              };

              handleProcessingStatusChange(updatedProcessingStatus.status, {
                ...record,
                processingStatus: updatedProcessingStatus,
              });
            }}
            style={{ width: 200 }}
            options={Object.values(WIRE_PAYMENT_PRISM_STATUSES).map(
              (status) => ({
                label: WIRE_PAYMENT_PRISM_STATUSES_TEXT[status],
                value: status,
              })
            )}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      title: 'PP Sale No.',
      dataIndex: ['bwPrism', 'profitPowerSaleId'],
      key: 'bwPrism.profitPowerSaleId',
      render: (value: string) => value || 'N/A',
    },
    {
      title: 'Processing Status',
      dataIndex: 'processingStatus',
      key: 'processingStatus',
      render: (
        processingStatus: {
          status: string;
          missingDocs: string[];
          externalStatuses: { PRISM: string };
        },
        record: WirePayment
      ) => {
        // Handle cases where processingStatus might be undefined
        const status =
          processingStatus?.status ||
          WIRE_PAYMENT_PROCESSING_STATUSES.PROCESSING;
        const missingDocs = processingStatus?.missingDocs || [];

        return (
          <Select
            value={status}
            onChange={(value) => handleProcessingStatusChange(value, record)}
            style={{ width: 200 }}
            options={Object.values(WIRE_PAYMENT_PROCESSING_STATUSES).map(
              (status) => ({
                label: WIRE_PAYMENT_PROCESSING_STATUSES_TEXT[status],
                value: status,
              })
            )}
            onClick={(e) => e.stopPropagation()}
          ></Select>
        );
      },
    },
    {
      title: 'Contract Care',
      dataIndex: ['bwPrism', 'contractCare'],
      key: 'bwPrism.contractCare',
      render: (value: boolean) =>
        value === true ? 'Yes' : value === false ? 'No' : 'N/A',
    },
    {
      title: 'Deposited Date',
      dataIndex: 'depositedDate',
      key: 'depositedDate',
      render: (date: string) => {
        if (!date) return '-'; // Handle cases where date might be null/undefined
        const d = new Date(date);
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const year = d.getFullYear().toString().slice(-2); // Get last 2 digits of year
        return `${month}-${day}-${year}`;
      },
    },
    {
      title: 'Rejected Date',
      dataIndex: 'rejectedDate',
      key: 'rejectedDate',
      render: (date: string) => {
        if (!date) return '-'; // Handle cases where date might be null/undefined
        const d = new Date(date);
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const year = d.getFullYear().toString().slice(-2); // Get last 2 digits of year
        return `${month}-${day}-${year}`;
      },
    },
  ];

  useEffect(() => {
    fetchWirePayments();
  }, []);

  const handleTableChange = (pagination: any) => {
    fetchWirePayments({
      ...filterParams,
      page: pagination.current,
    });
  };

  return (
    <div className="p-4 bg-[#F6F6F6]">
      <h2 className="text-2xl mb-4">Wire Payments</h2>
      <WirePaymentsFilter onFilter={handleFilter} />
      <Table
        columns={columns}
        dataSource={wirePayments}
        rowKey="_id"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: false,
        }}
        scroll={{ x: 'max-content' }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: (event) => {
            // Don't navigate if clicking on links or select elements
            if (
              event.target instanceof HTMLElement &&
              (event.target.closest('a') ||
                event.target.closest('.ant-select') ||
                event.target.closest('button'))
            ) {
              return;
            }
            navigate(AppURLs.complianceDashboardPaymentValidation(record._id));
          },
        })}
      />
    </div>
  );
};

export default WirePayments;
